export type Semver = {
    major: number;
    minor: number;
    patch: number;
    preRelease: string;
    environment: string;
    build: number;
    metadata: string;
}

export type ParseSemverResult = {
    success: boolean;
    semver?: Semver;
    error?: string;
}

/**
 * Parse a semver string with the format: vMAJOR.MINOR.PATCH-ENVIRONMENT.BUILD+metadata
 * Also supports partial formats like "MAJOR" or "MAJOR.MINOR"
 *
 * @param semverStr - The semverStr string to parse
 * @returns ParseSemverResult containing success status and parsed components or error message
 */
export function parseSemverString(semverStr: string): ParseSemverResult {
    if (!semverStr) {
        return {
            success: false,
            error: "Semver string is null or empty",
            semver: null
        };
    }

    // Updated regex to handle partial semver strings
    // Matches: MAJOR, MAJOR.MINOR, or MAJOR.MINOR.PATCH with optional pre-release and metadata
    const VERSION_REGEX = /^v?(0|[1-9][0-9]*)(?:\.(0|[1-9][0-9]*))?(?:\.(0|[1-9][0-9]*))?(-([A-Za-z]+(?:\.[0-9]+)?))?(\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;

    // Check for unexpected/hidden characters (whitespace)
    if (/\s/.test(semverStr)) {
        return {
            success: false,
            error: `Semver string contains whitespace or invalid characters: '${semverStr}'`
        };
    }

    // Validate against regex
    const match = semverStr.match(VERSION_REGEX);

    if (!match) {
        return {
            success: false,
            error: `Semver string '${semverStr}' does not match expected format.`
        };
    }

    const major = parseInt(match[1], 10);              // Major version as integer
    const minor = parseInt(match[2] || "0", 10);       // Minor version as integer (default to 0)
    const patch = parseInt(match[3] || "0", 10);       // Patch version as integer (default to 0)
    const preRelease = match[4] || "";                       // Full pre-release (e.g., "-env.1")
    const metadata = match[6] || "";                         // Full build metadata (e.g., "+build.123")

    let environment = "";
    let build = 0;  // Default to 0 for build number

    // Extract ENVIRONMENT (alphabetic part before the first period)
    if (preRelease) {
        const envMatch = preRelease.match(/^-([A-Za-z]+)/);
        if (envMatch) {
            environment = envMatch[1];
        }
    }

    // Extract BUILD (numeric part after the first period)
    if (preRelease) {
        const buildMatch = preRelease.match(/\.([0-9]+)$/);
        if (buildMatch) {
            build = parseInt(buildMatch[1], 10);  // Convert build to integer
        }
    }

    return {
        success: true,
        semver: {
            major,
            minor,
            patch,
            preRelease,
            environment,
            build,
            metadata
        }
    };
}

export function compareSemver(a: string | Semver, b: string | Semver): -1 | 0 | 1 {
    const aObj = typeof a === "string" ? (parseSemverString(a).semver ?? null) : a;
    const bObj = typeof b === "string" ? (parseSemverString(b).semver ?? null) : b;

    if (!aObj || !bObj) {
        throw new Error("compareSemver: invalid version(s)");
    }

    if (aObj.major !== bObj.major) return aObj.major < bObj.major ? -1 : 1;
    if (aObj.minor !== bObj.minor) return aObj.minor < bObj.minor ? -1 : 1;
    if (aObj.patch !== bObj.patch) return aObj.patch < bObj.patch ? -1 : 1;
    return 0;
}

export const isSemverEqual = (a: string | Semver, b: string | Semver) => compareSemver(a, b) === 0;
export const isSemverGreater = (a: string | Semver, b: string | Semver) => compareSemver(a, b) === 1;
export const isSemverLess = (a: string | Semver, b: string | Semver) => compareSemver(a, b) === -1;
export const isSemverGreaterOrEqual = (a: string | Semver, b: string | Semver) => compareSemver(a, b) >= 0;
export const isSemverLessOrEqual = (a: string | Semver, b: string | Semver) => compareSemver(a, b) <= 0;

export function satisfiesMinVersion(version: string | Semver, minimum: string | Semver): boolean {
    return isSemverGreaterOrEqual(version, minimum);
}